import { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom-v5-compat";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  TextField,
} from "@mui/material";
import useSettings from "../../hooks/useSettings";
import ChevronRightIcon from "../../icons/ChevronRight";
import gtm from "../../lib/gtm";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import { useSnackbar } from "notistack";

import PasswordInput from "@rlmonger/web_components/dist/Inputs/Password";

const Account = () => {
  const { settings } = useSettings();
  const { user, set_user } = useAuth();

  const [first_name, set_first_name] = useState("");
  const [last_name, set_last_name] = useState("");
  const [mobile, set_mobile] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const [password, set_password] = useState("");
  const [confirm_password, set_confirm_password] = useState("");

  const [loading, set_loading] = useState(false);

  const openaiTest = async () => {
    const { data } = await axios.post(
      `${settings.api}/v2/openai/test/custom-function`
    );
    console.log(data);
  };

  useEffect(() => {
   // console.log("TEST");
    //openaiTest();
  }, []);

  const reset_fields = useCallback(() => {
    set_first_name(user.first_name || "");
    set_last_name(user.last_name || "");
    set_mobile(user.mobile || "");
  }, [user.first_name, user.last_name, user.mobile]);

  const update_user = useCallback(async () => {
    set_loading(true);

    let { data: user } = await axios.patch(`${settings.api}/accounts/account`, {
      first_name,
      last_name,
      mobile,
    });

    // set_user(user);

    set_loading(false);
  }, [first_name, last_name, mobile]);

  const changesMade = useMemo(() => {
    if (user.first_name !== first_name) return true;
    if (user.last_name !== last_name) return true;
    if (user.mobile !== mobile) return true;
    return false;
  }, [
    user.first_name,
    user.last_name,
    user.mobile,
    first_name,
    last_name,
    mobile,
  ]);

  useEffect(() => {
    gtm.push({ event: "page_view" });
    reset_fields();
  }, []);

  return (
    <>
      <Helmet>
        <title>Dashboard: Account | MongerTracker GPS Tracking</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 8,
        }}
      >
        <Container maxWidth={settings.compact ? "xl" : false}>
          <Grid container justifyContent="space-between" spacing={3}>
            <Grid item>
              <Typography color="textPrimary" variant="h5">
                My Account
              </Typography>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
                sx={{ mt: 1 }}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to="/dashboard"
                  variant="subtitle2"
                >
                  Dashboard
                </Link>
                <Typography color="textSecondary" variant="subtitle2">
                  Account
                </Typography>
              </Breadcrumbs>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 3 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem />}
                  justifyContent="spaceBetween"
                  alignItems="center"
                  spacing={3}
                >
                  <Stack flexGrow={1} spacing={2} direction="column">
                    <Typography variant="h5">User Information</Typography>
                    <TextField
                      label="First Name"
                      value={first_name}
                      disabled={loading}
                      onChange={(e) => set_first_name(e.target.value)}
                    />
                    <TextField
                      label="Last Name"
                      value={last_name}
                      disabled={loading}
                      onChange={(e) => set_last_name(e.target.value)}
                    />
                    <TextField label="Email" disabled value={user.email} />
                    <TextField
                      label="Mobile"
                      value={mobile}
                      disabled={loading}
                      onChange={(e) => set_mobile(e.target.value)}
                    />
                  </Stack>
                  <Stack sx={{ mx: 5 }} alignItems="center">
                    <Avatar
                      src={`data:image/png;base64,${user.avatar}`}
                      sx={{
                        height: 150,
                        width: 150,
                      }}
                    />
                    {/*
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      align="center"
                      mt={1}
                    >
                      Change Profile Picture
                    </Typography>*/}
                  </Stack>
                </Stack>
                <Stack
                  mt={2}
                  spacing={2}
                  direction="row"
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    onClick={reset_fields}
                    disabled={loading || !changesMade}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={update_user}
                    disabled={loading || !changesMade}
                  >
                    Save Changes
                  </Button>
                </Stack>

                <Typography variant="h5">Change Password</Typography>

                <Divider sx={{ my: 2 }} />

                <Stack
                  direction="column"
                  justifyContent="spaceBetween"
                  alignItems="flex-end"
                  spacing={3}
                >
                  <PasswordInput
                    style={{ width: `100%` }}
                    formControlStyle={{ width: `100%` }}
                    label="Password"
                    className="w-100"
                    variant="outlined"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      set_password(e.target.value);
                    }}
                  />

                  <PasswordInput
                    style={{ width: `100%` }}
                    formControlStyle={{ width: `100%` }}
                    label="Confirm Password"
                    className="w-100"
                    variant="outlined"
                    placeholder="Confirm Password"
                    value={confirm_password}
                    onChange={(e) => {
                      set_confirm_password(e.target.value);
                    }}
                  />

                  <Button
                    disabled={!password || !confirm_password}
                    variant="contained"
                    onClick={async () => {
                      if (
                        password.length >= 3 &&
                        password === confirm_password
                      ) {
                        enqueueSnackbar("Password Changed", {
                          variant: "success",
                        });

                        await axios.post(
                          `${settings.api}/user/savenewpassword`,
                          {
                            password: password,
                            employee_id: user.username_id,
                          }
                        );

                        set_password("");
                        set_confirm_password("");
                      } else {
                        enqueueSnackbar(
                          "Password Does Not Much Or Is Less Than 3 Characters",
                          { variant: "error" }
                        );
                      }
                    }}
                  >
                    Change Password
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Account;
